import { useState, useEffect } from 'react';
import { ShieldCheck } from 'lucide-react';
import { apiCall } from './useAutoSave';

/**
 * Alergeny receptu jako čísla hned za názvem.
 *
 * Dřív se vypisovaly celé názvy („Celer a výrobky", „Oxid siřičitý a siřičitany")
 * a u položky se třemi alergeny zabraly víc místa než samotný název. Čísla podle
 * přílohy EU nařízení 1169/2011 jsou v gastru běžná zkratka a text se ukáže po
 * najetí — takže hlavička zůstane jednořádková a informace nezmizí.
 *
 * Bublina jde dolů: karta detailu má `overflow-hidden` a nahoře by se ořízla.
 */
export default function RecipeAlergenyInline({ recept, bomLength, refreshTick }) {
    const [alergeny, setAlergeny] = useState([]);
    const [najeto, setNajeto] = useState(null);

    useEffect(() => {
        let alive = true;
        if (recept?.id) {
            apiCall(`/api/recepty/recepty/${recept.id}/alergeny`)
                .then(d => { if (alive) setAlergeny(Array.isArray(d) ? d : (d?.alergeny || [])); })
                .catch(() => { if (alive) setAlergeny([]); });
        }
        return () => { alive = false; };
    }, [recept?.id, bomLength, refreshTick]);

    // Bez alergenů je taky informace — ale stačí na ni ikona, ne věta.
    if (alergeny.length === 0) {
        return (
            <ShieldCheck size={13} title="Bez alergenů" aria-label="Bez alergenů"
                style={{ color: '#22C55E', flexShrink: 0 }} />
        );
    }

    // Seřazeno podle čísla — číselná řada se čte rychleji než pořadí z kusovníku.
    const serazene = [...alergeny].sort((a, b) => (a.cislo_eu ?? 99) - (b.cislo_eu ?? 99));

    return (
        <span className="inline-flex items-center gap-1" style={{ flexShrink: 0 }}>
            {serazene.map((a, i) => {
                const cislo = a.cislo_eu ?? null;
                const popis = a.nazev || a.kod || String(a);
                const klic = a.id || a.kod || i;
                return (
                    <span key={klic} style={{ position: 'relative', display: 'inline-flex' }}
                        onMouseEnter={() => setNajeto(klic)} onMouseLeave={() => setNajeto(null)}
                        /* Klik kvůli dotyku — na tabletu se hover nespustí a bublina by byla nedostupná. */
                        onClick={() => setNajeto(k => (k === klic ? null : klic))}>
                        <span aria-label={`Alergen ${cislo ?? ''}: ${popis}`}
                            className="text-[10px] font-bold leading-none flex items-center justify-center"
                            style={{
                                width: 18, height: 18, borderRadius: '50%', cursor: 'help',
                                background: 'rgba(239,68,68,0.14)', color: '#EF4444',
                                border: '1px solid rgba(239,68,68,0.28)',
                            }}>
                            {cislo ?? '?'}
                        </span>
                        {najeto === klic && (
                            <span role="tooltip"
                                className="text-[11px] font-medium"
                                style={{
                                    position: 'absolute', top: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)',
                                    whiteSpace: 'nowrap', padding: '4px 8px', borderRadius: 5, zIndex: 30,
                                    background: 'var(--surface-overlay, #1f2937)', color: 'var(--text-primary, #fff)',
                                    border: '1px solid rgba(239,68,68,0.35)', boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
                                    pointerEvents: 'none',
                                }}>
                                {cislo ? `${cislo} · ${popis}` : popis}
                            </span>
                        )}
                    </span>
                );
            })}
        </span>
    );
}
